import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Loader2, Tag, Pencil, X, Star, Lock, Users } from "lucide-react";

const inp =
  "w-full rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";

const centsToDollars = (c) => (c || c === 0 ? (c / 100).toFixed(2) : "");

function priceFor(plan, interval) {
  const p = (plan?.prices || []).find((x) => x.interval === interval);
  return p ? centsToDollars(p.amount) : "";
}

function PlanEditorModal({ open, mode, plan, onClose, onSaved }) {
  const editing = mode === "edit";
  const [f, setF] = useState({ name: "", tagline: "", features: "", max_students: 30, monthly_amount: "", yearly_amount: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open && editing && plan) {
      setF({
        name: plan.name || "",
        tagline: plan.tagline || "",
        features: (plan.features || []).join("\n"),
        max_students: plan.max_students ?? 30,
        monthly_amount: priceFor(plan, "month"),
        yearly_amount: priceFor(plan, "year"),
      });
    } else if (open && !editing) {
      setF({ name: "", tagline: "", features: "", max_students: 30, monthly_amount: "", yearly_amount: "" });
    }
  }, [open, editing, plan]);

  const submit = async () => {
    if (!f.name.trim() || !f.monthly_amount) {
      toast.error("Name and monthly price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        tagline: f.tagline,
        max_students: Number(f.max_students) || 30,
        features: f.features.split("\n").map((x) => x.trim()).filter(Boolean),
        monthly_amount: Number(f.monthly_amount),
        yearly_amount: f.yearly_amount ? Number(f.yearly_amount) : null,
      };
      if (editing) {
        await api.updatePlan(plan.plan, payload);
        toast.success("Plan updated & synced to Stripe");
      } else {
        await api.createPlan(payload);
        toast.success("Plan created in Stripe & now on the pricing page");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-lg rounded-[28px] bg-white p-7 shadow-2xl border border-[#E5DECF] max-h-[90vh] overflow-y-auto" data-testid="plan-editor-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">{editing ? `Edit ${plan?.name}` : "New plan"}</h3>
              <button onClick={onClose} className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
            </div>
            {editing && plan?.builtin && (
              <p className="mt-2 rounded-xl bg-[#7A3FF2]/10 px-3 py-2 text-xs font-semibold text-[#652BDB]">Built-in plan — changes sync to Stripe and the public Pricing page.</p>
            )}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-bold">Name<input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="plan-name" className={inp} placeholder="Homeschool Co-op" /></label>
              <label className="text-sm font-bold">Tagline<input value={f.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="plan-tagline" className={inp} placeholder="For small groups" /></label>
              <label className="text-sm font-bold">Seat limit<input type="number" value={f.max_students} onChange={(e) => set("max_students", e.target.value)} data-testid="plan-seats" className={inp} /></label>
              <div />
              <label className="text-sm font-bold">Monthly price ($)<input type="number" step="0.01" value={f.monthly_amount} onChange={(e) => set("monthly_amount", e.target.value)} data-testid="plan-monthly" className={inp} placeholder="14.99" /></label>
              <label className="text-sm font-bold">Yearly price ($) <span className="font-normal text-[#94A3B8]">optional</span><input type="number" step="0.01" value={f.yearly_amount} onChange={(e) => set("yearly_amount", e.target.value)} data-testid="plan-yearly" className={inp} placeholder="143.90" /></label>
            </div>
            <label className="mt-3 block text-sm font-bold">Features (one per line)<textarea value={f.features} onChange={(e) => set("features", e.target.value)} data-testid="plan-features" className={`${inp} h-28`} placeholder={"Full PreK–8 curriculum\nUp to 10 kids\nProgress reports"} /></label>
            <button onClick={submit} disabled={saving} data-testid="plan-save" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : editing ? <Pencil size={16} /> : <Plus size={16} />} {editing ? "Save changes" : "Create plan"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-[#0D9488]" : "bg-[#CBD5E1]"}`} aria-label="Toggle active">
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, mode: "create", plan: null });
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const d = await api.adminPlans();
    setPlans(d.plans || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      await api.togglePlan(p.plan, !p.active);
      toast.success(`${p.name} ${p.active ? "hidden from" : "shown on"} the Pricing page`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (p) => {
    try { await api.deletePlan(p.plan); toast.success("Plan deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Platform Admin</div>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Subscription Plans</h1>
          <p className="mt-1 font-body text-[#6B7280]">Edit built-in plans or create new ones. Changes sync to Stripe and appear on the Pricing page.</p>
        </div>
        <button onClick={() => setEditor({ open: true, mode: "create", plan: null })} data-testid="new-plan-button" className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3.5 font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5">
          <Plus size={18} /> New plan
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {plans.map((p) => (
          <div key={p.plan} className={`rounded-3xl border p-5 transition-all ${p.active ? "border-[#E5DECF] bg-white" : "border-dashed border-[#CBD5E1] bg-[#F2ECE1]/40"}`} data-testid={`plan-card-${p.plan}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-lg font-extrabold text-[#1F2937] truncate">{p.name}</h3>
                  {p.popular && <span className="inline-flex items-center gap-1 rounded-full bg-[#FF9E1B]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#B45309]"><Star size={10} /> Popular</span>}
                  {p.builtin ? (
                    <span className="rounded-full bg-[#0D9488]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#0D9488]">Built-in</span>
                  ) : (
                    <span className="rounded-full bg-[#7A3FF2]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#652BDB]">Custom</span>
                  )}
                </div>
                {p.tagline && <p className="mt-1 font-body text-sm text-[#6B7280]">{p.tagline}</p>}
              </div>
              <Toggle on={p.active} onClick={() => toggle(p)} testid={`toggle-plan-${p.plan}`} />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-body text-sm">
              {(p.prices || []).map((pr) => (
                <span key={pr.lookup_key} className="font-heading font-extrabold text-[#0D9488]">{pr.label}</span>
              ))}
              <span className="inline-flex items-center gap-1 text-[#94A3B8]"><Users size={13} /> {p.max_students} {p.student_word || "seats"}</span>
            </div>

            {(p.features || []).length > 0 && (
              <ul className="mt-3 space-y-1">
                {p.features.slice(0, 4).map((ft, i) => (
                  <li key={i} className="flex items-start gap-1.5 font-body text-xs text-[#6B7280]"><Tag size={11} className="mt-0.5 shrink-0 text-[#0D9488]" /> {ft}</li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => setEditor({ open: true, mode: "edit", plan: p })} data-testid={`edit-plan-${p.plan}`} className="inline-flex items-center gap-1.5 rounded-xl bg-[#F2ECE1] px-3 py-2 text-sm font-bold text-[#1F2937] hover:bg-[#E5DECF]">
                <Pencil size={14} /> Edit
              </button>
              {p.builtin ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#94A3B8]"><Lock size={12} /> Can't delete</span>
              ) : confirmDel === p.plan ? (
                <span className="inline-flex items-center gap-2">
                  <button onClick={() => del(p)} data-testid={`del-plan-confirm-${p.plan}`} className="rounded-xl bg-[#E05A47] px-3 py-2 text-sm font-bold text-white hover:brightness-95">Confirm delete</button>
                  <button onClick={() => setConfirmDel(null)} className="rounded-xl px-2 py-2 text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1]">Cancel</button>
                </span>
              ) : (
                <button onClick={() => setConfirmDel(p.plan)} data-testid={`del-plan-${p.plan}`} className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold text-[#E05A47] hover:bg-[#E05A47]/10">
                  <Trash2 size={14} /> Delete
                </button>
              )}
              {!p.active && <span className="ml-auto text-xs font-bold text-[#94A3B8]">Hidden</span>}
            </div>
          </div>
        ))}
      </div>

      <PlanEditorModal open={editor.open} mode={editor.mode} plan={editor.plan} onClose={() => setEditor({ open: false, mode: "create", plan: null })} onSaved={load} />
    </div>
  );
}
